import React, { useMemo, useState } from 'react';
import { Typography, Box, Card } from '@mui/material';
import { useParams, useSearchParams } from 'react-router-dom';
import moment from 'moment';

import PageTemplate from 'src/components/PageTemplate';
import { useExpertManagement } from 'src/hooks/useExpertManagement';
import BackButton from 'src/components/BackButton';
import { useDecodedToken } from 'src/hooks/useDecodedToken';

// Import our new components and hooks
import { useReportsData } from 'src/hooks/useReportsData';
import { useExternalEfforts } from 'src/hooks/useExternalEfforts';
import { useQueryFiltering } from 'src/hooks/useQueryFiltering';
import { formatCompanyDisplayName, formatCompanyForApi, getExternalEffortsForCompany, calculateDateRanges } from 'src/utils/reportUtils';
import { useEffortsFiltering } from 'src/hooks/useEffortsFiltering';
import EffortsTable from 'src/pages/Reports/components/EffortsTable';
import NotesSection from 'src/pages/Reports/components/NotesSection';
import ExternalEffortModal from 'src/pages/Reports/components/ExternalEffortModal';
import ReportGenerationModal from 'src/pages/WinsDashboard/components/ReportGenerationModal';
import CompanyHeader from './components/CompanyHeader';

const WinsDashboardProfile = () => {
  const { companyID } = useParams();
  const [searchParams] = useSearchParams();
  const startDate = searchParams.get('startDate');
  const endDate = searchParams.get('endDate');
  const { experts, companiesWithActiveExperts } = useExpertManagement();
  const { decodedToken } = useDecodedToken();

  // State for report generation modal
  const [reportModalOpen, setReportModalOpen] = useState(false);

  // Calculate date ranges for the external efforts hook
  const dateRanges = useMemo(() => calculateDateRanges(startDate, endDate), [startDate, endDate]);

  // Custom hooks - pass dates to enable server-side filtering
  const { allQueries, allReports, isLoading, allReportsMutate, mutateAll } = useReportsData(startDate, endDate);

  const {
    externalEffortModalOpen,
    selectedCompanyForEffort,
    externalEffortForm,
    isSubmittingEffort,
    isEditMode,
    setExternalEffortModalOpen,
    handleOpenExternalEffortModal,
    handleOpenEditExternalEffortModal,
    handleSubmitExternalEffort,
    handleRemoveExternalEffort,
  } = useExternalEfforts();

  const { getCompanyQueries, getQueriesByExpert } = useQueryFiltering(
    allQueries,
    experts,
    null, // Not needed for single company view
    'custom',
    { custom: { start: moment(startDate), end: moment(endDate) } },
    ''
  );

  // Memoized company information
  const displayCompanyName = useMemo(() => {
    const name = formatCompanyDisplayName(companyID);
    // Exceptions: ensure proper casing for specific company names
    const nameExceptions = {
      Moosemoney: 'MooseMoney',
      'Seoplus+': 'seoplus+',
      Talentlab: 'TalentLab',
    };
    return nameExceptions[name] || name;
  }, [companyID]);
  const apiCompanyName = useMemo(() => formatCompanyForApi(companyID), [companyID]);

  // Get company-specific queries and group by expert
  const companyQueries = useMemo(() => {
    return getCompanyQueries(companyID, startDate, endDate);
  }, [getCompanyQueries, companyID, startDate, endDate]);

  const queriesByExpert = useMemo(() => {
    return getQueriesByExpert(companyQueries);
  }, [getQueriesByExpert, companyQueries]);

  // Get external efforts for this company
  const externalEfforts = useMemo(() => {
    return getExternalEffortsForCompany(allReports, displayCompanyName, startDate, endDate);
  }, [allReports, displayCompanyName, startDate, endDate]);

  // Use the filtering hook to get pre-filtered and sorted data
  const { winsData, effortsData } = useEffortsFiltering(queriesByExpert, externalEfforts);

  // Get company data for the report modal (single company)
  const modalCompanies = useMemo(() => {
    if (!companiesWithActiveExperts) return [];

    const companyData = companiesWithActiveExperts[displayCompanyName];
    if (!companyData?.hasActiveExperts) return [];

    return [
      {
        name: displayCompanyName,
        experts: companyData.experts.filter((expert) => expert.isActive === 1),
      },
    ];
  }, [companiesWithActiveExperts, displayCompanyName]);

  // Report generation handler
  const handleGenerateReport = () => {
    setReportModalOpen(true);
  };

  // External effort handlers
  const handleExternalEffortSubmit = (formData) => {
    handleSubmitExternalEffort(formData, allReports, allReportsMutate, dateRanges, 'custom');
  };

  const handleExternalEffortRemove = (company, effortId) => {
    handleRemoveExternalEffort(company, effortId, allReports, allReportsMutate, dateRanges, 'custom');
  };

  const handleExternalEffortEdit = (company, effort) => {
    handleOpenEditExternalEffortModal(company, effort);
  };

  const handleDateUpdate = () => {
    // Refresh all query data to reflect the updated reportDate
    mutateAll();
  };

  return (
    <PageTemplate loading={isLoading}>
      <BackButton />
      <Card
        elevation={0}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          bgcolor: 'white',
          borderRadius: 1,
          py: 4,
          border: '1px solid #ddd',
        }}
      >
        <CompanyHeader
          company={displayCompanyName}
          startDate={startDate}
          onOpenExternalEffortModal={handleOpenExternalEffortModal}
          onGenerateReport={handleGenerateReport}
        />

        {/* Wins Table - Only show if there are wins */}
        {winsData.hasData && (
          <Box sx={{ mt: 4, px: { xs: 2, sm: 4 } }}>
            <Typography variant="h6" sx={{ mb: 1, color: 'success.main' }}>
              Wins (Successful)
            </Typography>
            <EffortsTable
              filteredQueries={winsData.queries}
              filteredExternalEfforts={winsData.externalEfforts}
              onRemoveExternalEffort={(effortId) => handleExternalEffortRemove(displayCompanyName, effortId)}
              onEditExternalEffort={handleExternalEffortEdit}
              company={displayCompanyName}
              statusType="wins"
              showActions={true}
              onDateUpdate={handleDateUpdate}
              dateRanges={dateRanges}
              dateFilter="custom"
              allReports={allReports}
              allReportsMutate={allReportsMutate}
            />
          </Box>
        )}

        {/* PR Efforts Table - Only show if there are efforts */}
        {effortsData.hasData && (
          <Box sx={{ mt: 4, px: { xs: 2, sm: 4 } }}>
            <Typography variant="h6" sx={{ mb: 1, color: 'primary.main' }}>
              PR Efforts (In Progress)
            </Typography>
            <EffortsTable
              filteredQueries={effortsData.queries}
              filteredExternalEfforts={effortsData.externalEfforts}
              onRemoveExternalEffort={(effortId) => handleExternalEffortRemove(displayCompanyName, effortId)}
              onEditExternalEffort={handleExternalEffortEdit}
              company={displayCompanyName}
              statusType="inProgress"
              showActions={true}
              onDateUpdate={handleDateUpdate}
              dateRanges={dateRanges}
              dateFilter="custom"
              allReports={allReports}
              allReportsMutate={allReportsMutate}
            />
          </Box>
        )}
      </Card>

      {/* Notes Section */}
      <NotesSection
        companyName={apiCompanyName}
        startDate={startDate}
        endDate={endDate}
        allReports={allReports}
        allReportsMutate={allReportsMutate}
        decodedToken={decodedToken}
      />

      {/* External Effort Modal */}
      <ExternalEffortModal
        open={externalEffortModalOpen}
        onClose={() => setExternalEffortModalOpen(false)}
        selectedCompany={selectedCompanyForEffort}
        formData={externalEffortForm}
        onSubmit={handleExternalEffortSubmit}
        isSubmitting={isSubmittingEffort}
        isEditMode={isEditMode}
      />

      {/* Report Generation Modal */}
      <ReportGenerationModal
        open={reportModalOpen}
        onClose={() => setReportModalOpen(false)}
        companies={modalCompanies}
        dateRange={dateRanges.custom}
      />
    </PageTemplate>
  );
};

export default WinsDashboardProfile;
